import { academics } from "@/data/athlete";
import { SectionHeader } from "./_shared";
import { GraduationCap, BookOpen, Target, Compass, Users, FileText } from "lucide-react";

const Row = ({ icon: Icon, label, value, testId }) => (
  <div
    data-testid={testId}
    className="border border-zinc-800 bg-[#141414] p-5 md:p-6 flex items-start gap-4"
  >
    <Icon className="h-5 w-5 text-[#FF3B30] mt-0.5 shrink-0" />
    <div className="min-w-0">
      <div className="text-[10px] md:text-xs uppercase tracking-[0.25em] font-bold text-zinc-500 mb-1">
        {label}
      </div>
      <div className="text-white font-bold text-base md:text-lg leading-snug">
        {value}
      </div>
    </div>
  </div>
);

const AwardGroup = ({ title, items, testId }) => (
  <div data-testid={testId}>
    <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-zinc-400 mb-3">
      {title}
    </div>
    <ul className="space-y-2">
      {items.map((item, idx) => (
        <li
          key={`${testId}-${idx}`}
          className="text-white text-sm md:text-base flex gap-2 leading-snug"
        >
          <span className="text-[#FF3B30] mt-0.5 shrink-0">▸</span>
          <span className="min-w-0">
            {item.text}
            {item.url && (
              <>
                {" "}
                —{" "}
                <a
                  data-testid={`${testId}-${idx}-link`}
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(ev) => {
                    ev.preventDefault();
                    window.open(item.url, "_blank", "noopener,noreferrer");
                  }}
                  className="text-[#FF3B30] hover:text-[#FF5147] font-semibold underline-offset-2 hover:underline"
                >
                  {item.linkLabel || "Read More"}
                </a>
              </>
            )}
          </span>
        </li>
      ))}
    </ul>
  </div>
);

export const Academics = () => {
  return (
    <section
      id="academics"
      data-testid="academics-section"
      className="border-t border-zinc-900 py-16 md:py-24"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <SectionHeader
          eyebrow="05 — Off The Court"
          title="Academics"
          kicker="Student-first. A disciplined classroom record built alongside the gym."
          testId="academics-header"
        />

        <div className="grid lg:grid-cols-12 gap-4 md:gap-5">
          {/* GPA — most prominent */}
          <div
            data-testid="academics-gpa"
            className="lg:col-span-5 border border-zinc-800 bg-[#141414] p-6 md:p-8 flex flex-col justify-center"
          >
            <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#FF3B30]">
              GPA
            </div>
            <div className="font-display text-7xl md:text-8xl text-white mt-3 leading-none tabular-nums">
              {academics.gpa}
            </div>
            <div className="mt-3 text-xs uppercase tracking-wider text-zinc-500 font-semibold">
              {academics.gpaScale}
            </div>
          </div>

          {/* Right column: details rows */}
          <div className="lg:col-span-7 grid grid-cols-1 gap-4 md:gap-5">
            <Row
              icon={BookOpen}
              label="Coursework"
              value={academics.coursework}
              testId="academics-coursework"
            />
            <Row
              icon={Target}
              label="Academic Focus"
              value={academics.academicFocus}
              testId="academics-focus"
            />
            <Row
              icon={Compass}
              label="Intended Major"
              value={academics.intendedMajor}
              testId="academics-intended-major"
            />
            <div className="grid sm:grid-cols-2 gap-4 md:gap-5">
              <div
                data-testid="academics-activities"
                className="border border-zinc-800 bg-[#141414] p-5 md:p-6 flex items-start gap-4"
              >
                <Users className="h-5 w-5 text-[#FF3B30] mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <div className="text-[10px] md:text-xs uppercase tracking-[0.25em] font-bold text-zinc-500 mb-2">
                    Activities
                  </div>
                  <ul className="space-y-1.5">
                    {(Array.isArray(academics.activities)
                      ? academics.activities
                      : [academics.activities]
                    ).map((a) => (
                      <li
                        key={a}
                        className="text-white text-sm md:text-base flex gap-2 leading-snug"
                      >
                        <span className="text-[#FF3B30]">▸</span>
                        {a}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <Row
                icon={FileText}
                label="Test Scores"
                value={academics.testScores}
                testId="academics-test-scores"
              />
            </div>
          </div>
        </div>

        {/* Final positioning line */}
        <p
          data-testid="academics-positioning"
          className="mt-8 md:mt-10 text-sm md:text-base italic text-zinc-400 flex items-start gap-2"
        >
          <GraduationCap className="h-4 w-4 text-[#FF3B30] mt-1 shrink-0" />
          <span>{academics.positioningStatement}</span>
        </p>

        {/* Awards & Achievements */}
        <div
          data-testid="academics-awards"
          className="mt-8 md:mt-10 border border-zinc-800 bg-[#141414] p-6 md:p-8"
        >
          <div className="flex items-baseline gap-3 flex-wrap mb-5">
            <span className="text-xs uppercase tracking-[0.3em] font-bold text-[#FF3B30]">
              Awards & Achievements
            </span>
            <span className="h-px flex-1 bg-zinc-800 min-w-[40px]" />
          </div>

          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            <AwardGroup
              title="Academic Honors"
              items={academics.awards.academic}
              testId="awards-academic"
            />
            <AwardGroup
              title="Athletic Honors"
              items={academics.awards.athletic}
              testId="awards-athletic"
            />
            <AwardGroup
              title="Leadership & Achievement"
              items={academics.awards.leadership}
              testId="awards-leadership"
            />
          </div>
        </div>
      </div>
    </section>
  );
};
